import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Warning, ArrowLeft, Clock } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { stripeCheckStatus, paypalExecute } from '../services/api';

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [details, setDetails] = useState(null);

  const sessionId = searchParams.get('session_id');
  const gateway = searchParams.get('gateway');
  const paypalPaymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');
  const paymentId = searchParams.get('payment_id');

  useEffect(() => {
    const processPayment = async () => {
      // PayPal execute
      if (gateway === 'paypal' && paypalPaymentId && payerId) {
        try {
          const res = await paypalExecute({ paymentId: paypalPaymentId, PayerID: payerId, payment_id: paymentId });
          setStatus('success');
          setDetails(res.data);
        } catch {
          setStatus('error');
        }
        return;
      }

      // Stripe poll
      if (sessionId) {
        let attempts = 0;
        const maxAttempts = 5;
        const poll = async () => {
          if (attempts >= maxAttempts) {
            setStatus('timeout');
            return;
          }
          try {
            const res = await stripeCheckStatus(sessionId);
            if (res.data.payment_status === 'paid') {
              setStatus('success');
              setDetails(res.data);
              return;
            }
            if (res.data.status === 'expired') {
              setStatus('expired');
              return;
            }
          } catch {
            // continue polling
          }
          attempts++;
          setTimeout(poll, 2000);
        };
        poll();
        return;
      }

      setStatus('error');
    };

    processPayment();
  }, [sessionId, gateway, paypalPaymentId, payerId, paymentId]);

  const isPortal = window.location.pathname.includes('/portail');
  const backPath = isPortal ? '/portail/paiements' : '/payments';

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <Card className="border-[#E8E6E1] w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-[#2D5A4C] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Verification du paiement...</h2>
              <p className="text-sm text-[#5E6D66] mt-2">Veuillez patienter pendant que nous confirmons votre paiement.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle size={56} className="text-emerald-500 mx-auto mb-4" weight="fill" />
              <h2 className="text-xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope' }} data-testid="payment-success-title">Paiement confirme !</h2>
              <p className="text-sm text-[#5E6D66] mt-2">Votre paiement a ete traite avec succes. Le statut sera mis a jour automatiquement.</p>
              {details && details.amount_total && (
                <p className="text-lg font-bold text-[#1A2421] mt-3">
                  {(details.amount_total / 100).toLocaleString('fr-FR')} {(details.currency || 'EUR').toUpperCase()}
                </p>
              )}
              <Button onClick={() => navigate(backPath)} className="mt-6 bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="back-to-payments-btn">
                <ArrowLeft size={16} className="mr-2" />
                Retour aux paiements
              </Button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <Clock size={56} className="text-amber-500 mx-auto mb-4" weight="fill" />
              <h2 className="text-xl font-bold text-amber-700" style={{ fontFamily: 'Manrope' }}>Verification en cours</h2>
              <p className="text-sm text-[#5E6D66] mt-2">La confirmation prend un peu plus de temps. Votre paiement sera traite sous peu.</p>
              <Button onClick={() => navigate(backPath)} variant="outline" className="mt-6" data-testid="back-timeout-btn">
                <ArrowLeft size={16} className="mr-2" />
                Retour
              </Button>
            </>
          )}

          {(status === 'error' || status === 'expired') && (
            <>
              <Warning size={56} className="text-red-500 mx-auto mb-4" weight="fill" />
              <h2 className="text-xl font-bold text-red-700" style={{ fontFamily: 'Manrope' }} data-testid="payment-error-title">
                {status === 'expired' ? 'Session expiree' : 'Erreur de paiement'}
              </h2>
              <p className="text-sm text-[#5E6D66] mt-2">
                {status === 'expired' ? 'La session de paiement a expire. Veuillez reessayer.' : 'Une erreur est survenue. Veuillez reessayer ou contacter le support.'}
              </p>
              <Button onClick={() => navigate(backPath)} variant="outline" className="mt-6" data-testid="back-error-btn">
                <ArrowLeft size={16} className="mr-2" />
                Retour aux paiements
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
